// src/pages/Invite.jsx
import React, { Fragment, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import copy from "copy-to-clipboard";
import { useAPI } from "../context/ApiContext";
import { useTranslation } from "react-i18next";
import { FaLine, FaTwitter, FaWhatsapp, FaFacebookMessenger } from "react-icons/fa";

const isMobile = () =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const Invite = () => {
  const { t } = useTranslation();
  const api = useAPI();

  const PUBLIC_ORIGIN = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/+$/, "");
  const inviter = api?.myUserId || api?.me?.userId || "";
  // 招待リンク：landingpage に誘導（?invited_by=… 付き）
  const inviteUrl = useMemo(() => {
    const u = new URL(`${PUBLIC_ORIGIN}/landingpage`);
    if (inviter) u.searchParams.set("invited_by", inviter);
    return u.toString();
  }, [PUBLIC_ORIGIN, inviter]);

  const qrSrc = useMemo(() => {
    // 依存なしでQR作成（QuickChart）
    return `https://quickchart.io/qr?text=${encodeURIComponent(inviteUrl)}&margin=2&size=240`;
  }, [inviteUrl]);

  const shareTargets = useMemo(() => {
    const urlEnc = encodeURIComponent(inviteUrl);
    const textEnc = encodeURIComponent(t("InviteShareText") || "Nicelinksに招待されています👇");
    return {
      line: `https://line.me/R/msg/text/?${textEnc}%20${urlEnc}`,
      x: `https://twitter.com/intent/tweet?text=${textEnc}&url=${urlEnc}`,
      whatsapp: `https://api.whatsapp.com/send?text=${textEnc}%20${urlEnc}`,
      messenger: `https://www.facebook.com/dialog/send?app_id=87741124305&link=${urlEnc}&redirect_uri=${urlEnc}`,
    };
  }, [inviteUrl, t]);

  const tryWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Nicelinks",
          text: t("InviteShareText") || "Nicelinksに招待されています👇",
          url: inviteUrl,
        });
        return;
      } catch { /* cancel */ }
    }
    copy(inviteUrl);
    alert(t("InviteCopied") || "招待リンクをコピーしました");
  };

  const bg = isMobile() ? "/assets/images/invite_mobile.jpg" : "/assets/images/invite_pc.jpg";

  return (
    <Fragment>
      <Helmet>
        <title>Nicelinks | 招待リンク</title>
        <meta property="og:title" content="Nicelinks | 招待リンク" />
        <meta name="twitter:title" content="Nicelinks | 招待リンク" />
        {/* 招待はランディングへ送る */}
        <link rel="canonical" href={`${PUBLIC_ORIGIN}/landingpage`} />
      </Helmet>

      <div
        className="main-wrap"
        style={{
          minHeight: "100vh",
          backgroundImage: `url("${bg}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* 透過グラスカード */}
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "100vh", backdropFilter: "saturate(1.2) blur(0px)" }}
        >
          <div
            className="card shadow-lg"
            style={{
              width: "min(92vw, 560px)",
              borderRadius: 20,
              background: "rgba(255,255,255,.92)",
              overflow: "hidden",
            }}
          >
            <div className="card-body p-4 text-center">
              <img src="/assets/images/nicelinks_icon.png" alt="logo" style={{ height: 36 }} />
              <h2 className="fw-700 mt-2 mb-1">{t("Invite1") || "Nicelinksに友達を招待しよう！"}</h2>
              <div className="text-grey-600 font-xssss mb-3">
                {t("InviteLead") || "QRをスキャンするか、リンクをコピー・シェアしてください"}
              </div>

              {/* QR */}
              <div className="d-flex justify-content-center mb-3">
                <img
                  src={qrSrc}
                  alt="QR"
                  className="shadow-sm"
                  style={{ width: 220, height: 220, borderRadius: 16, background: "#fff", padding: 8 }}
                />
              </div>

              {/* URL（小さく見せる） */}
              <div
                className="text-truncate font-xssss"
                style={{
                  maxWidth: 460,
                  margin: "0 auto 12px",
                  color: "#374151",
                }}
                title={inviteUrl}
              >
                {inviteUrl}
              </div>

              {/* ボタン群 */}
              <div className="d-grid gap-2 mb-2">
                <button
                  className="btn bg-current text-white fw-700"
                  style={{ borderRadius: 9999 }}
                  onClick={() => { copy(inviteUrl); alert(t("InviteCopied") || "招待リンクをコピーしました"); }}
                >
                  <i className="feather-copy me-2" />
                  {t("InviteCopy") || "クリックして招待リンクをコピー"}
                </button>

                <button
                  className="btn btn-dark text-white fw-700"
                  style={{ borderRadius: 9999 }}
                  onClick={tryWebShare}
                >
                  <i className="feather-share-2 me-2" />
                  {t("InviteShare") || "端末の共有メニューを開く"}
                </button>
              </div>

              {/* SNSショートカット */}
              <div className="d-flex justify-content-center" style={{ gap: 12 }}>
                <a href={shareTargets.line} target="_blank" rel="noopener noreferrer" title="LINE" aria-label="LINE">
                  <FaLine size={22} color="#00c300" />
                </a>
                <a href={shareTargets.x} target="_blank" rel="noopener noreferrer" title="X" aria-label="X">
                  <FaTwitter size={22} color="#1DA1F2" />
                </a>
                <a href={shareTargets.whatsapp} target="_blank" rel="noopener noreferrer" title="WhatsApp" aria-label="WhatsApp">
                  <FaWhatsapp size={22} color="#25D366" />
                </a>
                <a href={shareTargets.messenger} target="_blank" rel="noopener noreferrer" title="Messenger" aria-label="Messenger">
                  <FaFacebookMessenger size={22} color="#0084FF" />
                </a>
              </div>

              <div className="mt-3">
                <Link to="/home" className="font-xssss text-decoration-underline">ホームへ戻る</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Invite;
